/**
 * SpreadsheetStatusBar.jsx
 * Bottom status bar — row/column count, selection info, formula worker status,
 * edit-mode indicator.
 */

import React, { memo } from 'react';
import { Shield, Zap } from 'lucide-react';
import { useFormulaStore, useSheetStore } from '../../../libraries/spreadsheet/store/spreadsheetStores';

const SpreadsheetStatusBar = memo(({ sheetId, columns, canEdit, rowCount, selectedCount }) => {
  const workerReady       = useFormulaStore((s) => s.workerReady);
  const activeCellAddress = useFormulaStore((s) => s.activeCellAddress);
  const dirtyCount = useSheetStore((s) => (s.dirtyRowsMap[sheetId] || new Set()).size);
  const errorCount = useSheetStore((s) => {
    const em = s.errorMap[sheetId] || {};
    return Object.values(em).reduce((sum, e) => sum + Object.keys(e).length, 0);
  });

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      height: 24, padding: '0 14px',
      background: '#1e3a5f', color: '#93c5fd',
      fontSize: 10, fontFamily: "'DM Sans', sans-serif",
      flexShrink: 0, gap: 16, userSelect: 'none',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <span>{(rowCount || 0).toLocaleString()} rows</span>
        <span>{(columns || []).length} columns</span>
        {selectedCount > 0 && <span style={{ color: '#60a5fa' }}>{selectedCount} selected</span>}
        {activeCellAddress && (
          <span style={{ color: '#bfdbfe', fontFamily: "'DM Mono', monospace" }}>
            Cell: {activeCellAddress}
          </span>
        )}
        {dirtyCount > 0 && <span style={{ color: '#fbbf24' }}>{dirtyCount} unsaved</span>}
        {errorCount > 0 && <span style={{ color: '#f87171' }}>{errorCount} errors</span>}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Zap size={9} color={workerReady ? '#34d399' : '#f87171'} />
          <span style={{ color: workerReady ? '#34d399' : '#f87171' }}>
            Formula engine {workerReady ? 'ready' : 'loading'}
          </span>
        </div>
        <span style={{ color: '#475569' }}>|</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Shield size={9} color={canEdit ? '#34d399' : '#f87171'} />
          <span style={{ color: canEdit ? '#34d399' : '#f87171' }}>
            {canEdit ? 'Edit mode' : 'Read only'}
          </span>
        </div>
        <span style={{ color: '#475569' }}>|</span>
        <span style={{ color: '#64748b' }}>Ctrl+S to save</span>
      </div>
    </div>
  );
});

export default SpreadsheetStatusBar;
